/**
 * Iter209af — Impostazioni Email.
 * Iter209an — Firma email come rich text (HTML) persistita sul profilo
 * utente e iniettata nel compose come HTML.
 *
 * Contiene: nome visualizzato, firma (HTML), risponditore automatico,
 * notifiche browser, cambio password casella, dati IMAP/SMTP read-only.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Eye, FloppyDisk } from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";

export default function EmailSettings() {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [signature, setSignature] = useState("");
  const [savingSig, setSavingSig] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [autoResponder, setAutoResponder] = useState(false);
  const [autoResponderText, setAutoResponderText] = useState("");
  const [autoStart, setAutoStart] = useState("");
  const [autoEnd, setAutoEnd] = useState("");
  const [browserNotif, setBrowserNotif] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwdConfirm, setNewPwdConfirm] = useState("");

  useEffect(() => {
    if (user?.email_signature_html !== undefined) {
      setSignature(user.email_signature_html || "");
    }
    // Iter209ca — Carica il nome visualizzato dal profilo al mount /
    // ad ogni refresh utente, così persiste tra reload della pagina.
    if (user?.email_display_name !== undefined) {
      setDisplayName(user.email_display_name || "");
    }
  }, [user?.email_signature_html, user?.email_display_name]);

  const saveSignature = async () => {
    setSavingSig(true);
    try {
      // Iter209ca — Il tasto "Salva firma" ora salva sia il nome
      // visualizzato sia la firma HTML in un'unica PATCH.
      await api.patch("/auth/profile", {
        email_display_name: displayName,
        email_signature_html: signature,
      });
      toast.success("Nome visualizzato e firma salvati");
      if (typeof refreshUser === "function") await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingSig(false);
    }
  };

  const imap = {
    server: "imap.housetag.it",
    port: 993,
    security: "SSL/TLS",
    username: "utente@tuodominio.it",
  };
  const smtp = {
    server: "smtp.housetag.it",
    port: 465,
    security: "SSL/TLS",
    username: "utente@tuodominio.it",
  };

  const Section = ({ title, desc, children, testId }) => (
    <div className="kpi-card space-y-3" data-testid={testId}>
      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
        {desc && <p className="text-xs text-slate-500 mt-1">{desc}</p>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="email-settings-page">
      <div className="flex items-center gap-2">
        <Link to="/email" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="email-settings-back">
          <ArrowLeft size={14} /> Torna alla casella
        </Link>
      </div>
      <div>
        <div className="label-eyebrow text-purple-600">Personalizzazione</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Impostazioni email</h1>
      </div>

      <Section title="Nome visualizzato e firma" desc="Nome mostrato ai destinatari e firma HTML allegata automaticamente a ogni nuova email." testId="email-settings-signature">
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-700">Nome visualizzato</label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="email-display-name" placeholder="Es. Studio Rossi & Bianchi" autoComplete="off" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-700">Firma (rich text)</label>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowPreview((p) => !p)}
                data-testid="email-signature-toggle-preview"
              >
                <Eye size={14} /> {showPreview ? "Nascondi anteprima" : "Anteprima"}
              </Button>
              <Button
                size="sm"
                onClick={saveSignature}
                disabled={savingSig}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="email-signature-save"
              >
                <FloppyDisk size={14} /> {savingSig ? "Salvataggio…" : "Salva firma"}
              </Button>
            </div>
          </div>
          <RichTextEditor
            value={signature}
            onChange={setSignature}
            placeholder="Cordiali saluti, ..."
            minHeight={200}
            testId="email-signature-editor"
          />
          {showPreview && (
            <div className="mt-2 rounded-md border border-slate-200 bg-white p-4" data-testid="email-signature-preview">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 font-bold">Anteprima firma</div>
              {signature ? (
                <div
                  className="ql-editor prose prose-sm max-w-none"
                  data-testid="email-signature-preview-html"
                  dangerouslySetInnerHTML={{ __html: signature }}
                />
              ) : (
                <div className="text-sm text-slate-400 italic">La firma è vuota. Scrivi qualcosa nell'editor per vedere l'anteprima.</div>
              )}
            </div>
          )}
        </div>
      </Section>

      <Section title="Risponditore automatico" desc="Risponde automaticamente alle email ricevute durante la tua assenza." testId="email-settings-auto-responder">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-700">Attivo</span>
          <Switch checked={autoResponder} onCheckedChange={setAutoResponder} data-testid="email-auto-toggle" />
        </div>
        {autoResponder && (
          <div className="space-y-2">
            <Textarea rows={4} value={autoResponderText} onChange={(e) => setAutoResponderText(e.target.value)} data-testid="email-auto-text" placeholder="Grazie per la tua email. Sono fuori ufficio fino al …" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Inizio</label>
                <Input type="date" value={autoStart} onChange={(e) => setAutoStart(e.target.value)} data-testid="email-auto-start" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Fine</label>
                <Input type="date" value={autoEnd} onChange={(e) => setAutoEnd(e.target.value)} data-testid="email-auto-end" />
              </div>
            </div>
          </div>
        )}
      </Section>

      <Section title="Notifiche" desc="Ricevi un avviso browser per ogni nuova email." testId="email-settings-notifications">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-700">Notifiche browser</span>
          <Switch checked={browserNotif} onCheckedChange={setBrowserNotif} data-testid="email-notif-toggle" />
        </div>
      </Section>

      <Section title="Cambio password casella" desc="Modifica la password d'accesso a questa casella email." testId="email-settings-password">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input type="password" placeholder="Password attuale" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} data-testid="email-pwd-current" />
          <Input type="password" placeholder="Nuova password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="email-pwd-new" />
          <Input type="password" placeholder="Conferma nuova" value={newPwdConfirm} onChange={(e) => setNewPwdConfirm(e.target.value)} data-testid="email-pwd-confirm" />
        </div>
        <Button className="ht-gradient border-0 w-fit" data-testid="email-pwd-save">Aggiorna password</Button>
      </Section>

      <Section title="Configurazione client esterni (IMAP / SMTP)" desc="Dati di sola lettura per configurare Outlook, Apple Mail, Thunderbird…" testId="email-settings-imap-smtp">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="text-xs uppercase text-slate-500 font-bold">IMAP (in entrata)</div>
            <ReadonlyRow label="Server" value={imap.server} testId="imap-server" />
            <ReadonlyRow label="Porta" value={String(imap.port)} testId="imap-port" />
            <ReadonlyRow label="Sicurezza" value={imap.security} testId="imap-security" />
            <ReadonlyRow label="Username" value={imap.username} testId="imap-username" />
          </div>
          <div className="space-y-1">
            <div className="text-xs uppercase text-slate-500 font-bold">SMTP (in uscita)</div>
            <ReadonlyRow label="Server" value={smtp.server} testId="smtp-server" />
            <ReadonlyRow label="Porta" value={String(smtp.port)} testId="smtp-port" />
            <ReadonlyRow label="Sicurezza" value={smtp.security} testId="smtp-security" />
            <ReadonlyRow label="Username" value={smtp.username} testId="smtp-username" />
          </div>
        </div>
      </Section>
    </div>
  );
}

function ReadonlyRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-1">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-mono text-xs text-slate-800" data-testid={testId}>{value}</span>
    </div>
  );
}
